import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

/* ═══════════════════════════════════════════════════
   3D store: colored stocked shelves, 17 shopper
   agents on collision-safe paths, orbit + zoom.
   ═══════════════════════════════════════════════════ */

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function StoreScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const W = () => mount.clientWidth || 600;
    const H = () => mount.clientHeight || 460;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x070a12);
    scene.fog = new THREE.Fog(0x070a12, 70, 160);

    const camera = new THREE.PerspectiveCamera(50, W() / H(), 0.1, 300);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W(), H());
    mount.appendChild(renderer.domElement);

    const resize = () => {
      camera.aspect = W() / H();
      camera.updateProjectionMatrix();
      renderer.setSize(W(), H());
    };
    window.addEventListener('resize', resize);
    resize();

    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    scene.add(new THREE.HemisphereLight(0xbfd4ff, 0x30281e, 0.5));
    const key = new THREE.DirectionalLight(0xfff4e0, 0.85);
    key.position.set(20, 40, 20);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x88aaff, 0.35);
    fill.position.set(-30, 15, -20);
    scene.add(fill);

    // orbit camera
    const cam = { theta: -0.6, phi: 1.05, r: 46, target: new THREE.Vector3(4, 0, 4) };
    const applyCam = () => {
      camera.position.set(
        cam.target.x + cam.r * Math.sin(cam.phi) * Math.sin(cam.theta),
        cam.target.y + cam.r * Math.cos(cam.phi),
        cam.target.z + cam.r * Math.sin(cam.phi) * Math.cos(cam.theta)
      );
      camera.lookAt(cam.target);
    };
    applyCam();

    let dragging = false, lastX = 0, lastY = 0;
    const el = renderer.domElement;
    el.style.cursor = 'grab';
    const onDown = (e) => { dragging = true; lastX = e.clientX; lastY = e.clientY; el.style.cursor = 'grabbing'; };
    const onUp = () => { dragging = false; el.style.cursor = 'grab'; };
    const onMove = (e) => {
      if (!dragging) return;
      cam.theta -= (e.clientX - lastX) * 0.005;
      cam.phi = Math.min(1.45, Math.max(0.25, cam.phi - (e.clientY - lastY) * 0.004));
      lastX = e.clientX; lastY = e.clientY;
      applyCam();
    };
    const onWheel = (e) => {
      e.preventDefault();
      cam.r = Math.min(90, Math.max(14, cam.r + e.deltaY * 0.03));
      applyCam();
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('wheel', onWheel, { passive: false });

    // materials
    const matFloor = new THREE.MeshStandardMaterial({ color: 0x151a24, roughness: 0.9 });
    const matWall = new THREE.MeshStandardMaterial({ color: 0x2a3244, roughness: 0.85 });
    const matShelf = new THREE.MeshStandardMaterial({ color: 0x46536e, roughness: 0.7, metalness: 0.15 });
    const matGrid = new THREE.LineBasicMaterial({ color: 0x24304a });
    const zoneMat = (hex) =>
      new THREE.MeshStandardMaterial({ color: hex, transparent: true, opacity: 0.22, roughness: 0.6 });

    const store = new THREE.Group();
    scene.add(store);

    const addBox = (w, h, d, x, y, z, mat, parent = store) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      parent.add(m);
      return m;
    };

    // floor + grid
    addBox(40, 0.3, 30, 0, -0.15, 0, matFloor);
    const gridPts = [];
    for (let x = -20; x <= 20; x += 2) gridPts.push(x, 0.02, -15, x, 0.02, 15);
    for (let z = -15; z <= 15; z += 2) gridPts.push(-20, 0.02, z, 20, 0.02, z);
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(gridPts, 3));
    store.add(new THREE.LineSegments(gridGeo, matGrid));

    // walls
    addBox(40, 5, 0.4, 0, 2.5, -15, matWall);
    addBox(0.4, 5, 30, -20, 2.5, 0, matWall);
    addBox(0.4, 5, 30, 20, 2.5, 0, matWall);
    addBox(22, 5, 0.4, -9, 2.5, 15, matWall);
    addBox(6, 5, 0.4, 8, 2.5, 15, matWall);
    addBox(6, 5, 0.4, 17, 2.5, 15, matWall);

    // colored department trim
    const trimMat = (hex) => new THREE.MeshBasicMaterial({ color: hex });
    addBox(11, 0.35, 0.55, -14.5, 4.7, -14.7, trimMat(0x10b981));
    addBox(14, 0.35, 0.55, 4, 4.7, -14.7, trimMat(0x60a5fa));
    addBox(0.55, 0.35, 12, 19.7, 4.7, -8, trimMat(0xa78bfa));

    // entrance
    addBox(3, 0.12, 0.6, -5, 0.06, 14.7, trimMat(0x10b981));
    const portal = addBox(3.4, 4.6, 0.15, -5, 2.3, 15.1,
      new THREE.MeshBasicMaterial({ color: 0x10b981, transparent: true, opacity: 0.14 }));

    // department zones
    addBox(9, 0.05, 4, -14, 0.03, -12, zoneMat(0x10b981));
    addBox(12, 0.05, 3.5, 4, 0.03, -13, zoneMat(0x60a5fa));
    addBox(4, 0.05, 10, 17.5, 0.03, -6, zoneMat(0xa78bfa));

    // aisles + stocked colored products
    const aisleX = [-12, -4, 4, 12];
    const PALETTES = [
      [0xef4444, 0xf59e0b, 0x84cc16, 0x22c55e, 0xfacc15],
      [0x60a5fa, 0x93c5fd, 0xe2e8f0, 0x38bdf8, 0xc7d2fe],
      [0xf59e0b, 0xf97316, 0xef4444, 0xfbbf24, 0xfb7185],
      [0xa78bfa, 0x22d3ee, 0x818cf8, 0x67e8f9, 0xc4b5fd],
    ];
    const productMats = PALETTES.map((pal) =>
      pal.map((h) => new THREE.MeshStandardMaterial({ color: h, roughness: 0.45 }))
    );

    const OBST = [];
    aisleX.forEach((ax, ai) => {
      [-1.4, 1.4].forEach((off) => {
        const sx = ax + off;
        addBox(0.9, 3.2, 18, sx, 1.9, -2, matShelf);
        OBST.push({ minX: sx - 0.45, maxX: sx + 0.45, minZ: -11, maxZ: 7 });

        const rand = mulberry32(ai * 733 + (off > 0 ? 91 : 17));
        const face = off > 0 ? 1 : -1;
        for (let lvl = 0; lvl < 4; lvl++) {
          const y = 0.75 + lvl * 0.72;
          for (let z = -10.2; z <= 6.2; z += 1.15) {
            if (rand() < 0.18) continue;
            const pw = 0.55 + rand() * 0.35;
            const ph = 0.4 + rand() * 0.22;
            const mat = productMats[ai][Math.floor(rand() * productMats[ai].length)];
            addBox(0.32, ph, pw, sx + face * 0.62, y + ph / 2, z + rand() * 0.2, mat);
          }
        }
      });

      // floating aisle label
      const canvas = document.createElement('canvas');
      canvas.width = 128; canvas.height = 64;
      const c2 = canvas.getContext('2d');
      c2.fillStyle = 'rgba(255,255,255,.7)';
      c2.font = '600 40px monospace';
      c2.textAlign = 'center';
      c2.fillText('A' + (ai + 1), 64, 46);
      const lbl = new THREE.Mesh(
        new THREE.PlaneGeometry(2.4, 1.2),
        new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true })
      );
      lbl.position.set(ax, 4.6, -2);
      lbl.rotation.x = -Math.PI / 6;
      store.add(lbl);
    });

    // checkout lanes
    const laneMat = new THREE.MeshStandardMaterial({ color: 0x5b6b8c, roughness: 0.6, metalness: 0.2 });
    const beltMat = new THREE.MeshStandardMaterial({ color: 0x1f2735, roughness: 0.9 });
    [10, 13, 16].forEach((z) => {
      addBox(0.6, 1.1, 2.2, 7, 0.85, z, laneMat);
      addBox(0.5, 0.15, 3.5, 7, 1.45, z, beltMat);
      OBST.push({ minX: 6.6, maxX: 7.4, minZ: z - 1.2, maxZ: z + 1.2 });
    });

    // shopper cohorts (aisle-safe paths)
    const COHORTS = [
      { name: 'family', color: 0xf59e0b, count: 4, speed: 1.1, path: [
        [-5, 13.5, 0], [-12, 8.5, 0], [-12, -9, 2.5],
        [-12, -13, 0], [-4, -13, 0], [-4, 2, 2.5],
        [-4, 8.5, 0], [4, 8.5, 0], [4, -9, 0],
        [4, -13, 2.5], [12, -13, 0], [12, 3, 2.5],
        [12, 8.5, 0], [10, 12, 0], [7, 12.5, 0],
        [-5, 13.5, 0]] },
      { name: 'topup', color: 0x60a5fa, count: 6, speed: 1.4, path: [
        [-5, 13.5, 0], [-4, 8.5, 0], [-4, -8, 2], [-4, 8.5, 0],
        [4, 8.5, 0], [4, -6, 2], [4, 8.5, 0],
        [12, 8.5, 0], [12, -4, 1.5], [12, 8.5, 0],
        [9, 12, 0], [-5, 13.5, 0]] },
      { name: 'health', color: 0x10b981, count: 3, speed: 1.2, path: [
        [-5, 13.5, 0], [-17, 8.5, 0], [-17, -13.5, 0],
        [-14, -12, 3],
        [-9, -13.5, 0], [4, -13.5, 2],
        [4, 8.5, 0], [10, 12, 0], [-5, 13.5, 0]] },
      { name: 'frozen', color: 0xa78bfa, count: 3, speed: 1.6, path: [
        [-5, 13.5, 0], [9, 12, 0], [16.5, 8, 1.5],
        [16.5, -11, 2.5], [16.5, 8, 0],
        [9, 12, 0], [-5, 13.5, 0]] },
      { name: 'anomaly', color: 0xef4444, count: 1, speed: 1.0, path: [
        [-5, 13.5, 0], [-4, 8.5, 0], [4, 8.5, 0],
        [4, 2, 0], [4.5, -5, 22],
        [4, 8.5, 0], [-5, 13.5, 0]] },
    ];

    const shoppers = [];
    COHORTS.forEach((c) => {
      for (let i = 0; i < c.count; i++) {
        const mat = new THREE.MeshStandardMaterial({
          color: c.color, roughness: 0.5, emissive: c.color, emissiveIntensity: 0.25,
        });
        const body = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.3, 1.0, 10), mat);
        body.position.y = 0.5;
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), mat);
        head.position.y = 1.22;
        const g = new THREE.Group();
        g.add(body); g.add(head);
        if (c.name === 'anomaly') {
          const halo = new THREE.Mesh(
            new THREE.RingGeometry(0.55, 0.75, 24),
            new THREE.MeshBasicMaterial({ color: c.color, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
          );
          halo.rotation.x = -Math.PI / 2;
          halo.position.y = 0.06;
          g.add(halo);
        }
        g.position.set(c.path[0][0], 0, c.path[0][1]);
        store.add(g);
        shoppers.push({ obj: g, cohort: c, wp: (i * 3) % (c.path.length - 1), dwellLeft: 0, bobPhase: i * 1.7 });
      }
    });

    // collision safety net
    const PAD = 0.35;
    function resolveCollision(p) {
      for (let k = 0; k < OBST.length; k++) {
        const o = OBST[k];
        if (p.x > o.minX - PAD && p.x < o.maxX + PAD &&
            p.z > o.minZ - PAD && p.z < o.maxZ + PAD) {
          const dl = p.x - (o.minX - PAD), dr = (o.maxX + PAD) - p.x;
          const db = p.z - (o.minZ - PAD), df = (o.maxZ + PAD) - p.z;
          const m = Math.min(dl, dr, db, df);
          if (m === dl) p.x = o.minX - PAD;
          else if (m === dr) p.x = o.maxX + PAD;
          else if (m === db) p.z = o.minZ - PAD;
          else p.z = o.maxZ + PAD;
        }
      }
    }

    const v = new THREE.Vector3();
    function step(dt) {
      shoppers.forEach((s) => {
        const p = s.cohort.path;
        if (s.dwellLeft > 0) { s.dwellLeft -= dt; return; }
        const target = p[s.wp + 1] || p[0];
        v.set(target[0] - s.obj.position.x, 0, target[1] - s.obj.position.z);
        const dist = v.length();
        const move = s.cohort.speed * dt;
        if (dist <= move) {
          s.obj.position.x = target[0];
          s.obj.position.z = target[1];
          s.dwellLeft = target[2] || 0;
          s.wp = (s.wp + 1) % (p.length - 1);
        } else {
          v.normalize();
          s.obj.position.x += v.x * move;
          s.obj.position.z += v.z * move;
          s.obj.rotation.y = Math.atan2(v.x, v.z);
        }
        resolveCollision(s.obj.position);
        s.obj.position.y = Math.abs(Math.sin(performance.now() * 0.006 + s.bobPhase)) * 0.05;
      });
    }

    let last = performance.now();
    let raf;
    function loop(now) {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!dragging) step(dt);
      renderer.render(scene, camera);
    }
    raf = requestAnimationFrame(loop);

    // cleanup — important for router navigation + StrictMode double-mount
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('wheel', onWheel);
      renderer.dispose();
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#070a12]">
      <div ref={mountRef} className="w-full h-[460px]" />
      <div className="absolute left-3 bottom-2 font-mono text-[10px] tracking-widest uppercase text-white/30 pointer-events-none">
        Drag to orbit · scroll to zoom
      </div>
      <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 pointer-events-none">
        <span className="font-mono text-[10px] tracking-wider uppercase bg-black/80 border border-white/10 rounded px-2 py-0.5">
          <span className="text-emerald-400">●</span> In store · 47
        </span>
        <span className="font-mono text-[10px] tracking-wider uppercase text-amber-400 bg-black/80 border border-white/10 rounded px-2 py-0.5">
          Family · 12 · avg 22 min
        </span>
        <span className="font-mono text-[10px] tracking-wider uppercase text-blue-400 bg-black/80 border border-white/10 rounded px-2 py-0.5">
          Top-up · 18 · avg 8 min
        </span>
        <span className="font-mono text-[10px] tracking-wider uppercase text-red-400 bg-black/80 border border-white/10 rounded px-2 py-0.5">
          Aisle 3 dwell ▲ 18%
        </span>
      </div>
    </div>
  );
}

export default StoreScene;
